import { Box, Container, Stack, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { styled, useTheme } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import PageSectionBG from "../PageSectionBG";

const pxToRem = (px: number) => `${px / 16}rem`;

const TestimonialsSection = () => {
  const theme = useTheme();
  const [hoverSide, setHoverSide] = useState<null | "left" | "right">(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [direction, setDirection] = useState<number>(-1);

  const client_testimonials = [
    {
      quote:
        "We at Thrifty Traveler are delighted with our app's redesign. Their team expertly transformed our platform into a more intuitive and user-friendly interface, significantly boosting user engagement and subscription conversions by <span>40% and 30%</span>, respectively. The feedback from our users has been overwhelmingly positive. 'Pencil to Polish' not only revitalized our app but also our brand's digital presence, making them a pivotal partner in our ongoing success.",
      name: "Jared K.",
      company: "Thrifty Traveler",
    },
    {
      quote:
        "Working with 'Pencil to Polish' was a game-changer for our business. Their expertise in UI/UX design helped us create a stunning website that exceeded our expectations. The user engagement and conversion rates have <span>skyrocketed since the launch</span>. We highly recommend their services.",
      name: "Emily S.",
      company: "Tech Solutions Inc.",
    },
    {
      quote:
        "I can't thank 'Pencil to Polish' enough for their exceptional work. They transformed our outdated app into a modern and intuitive platform. The new design has received rave reviews from our users, and our revenue has <span>increased by 50%</span>. It was a pleasure working with their talented team.",
      name: "Alex M.",
      company: "Innovative Startups",
    },
    {
      quote:
        "The team at 'Pencil to Polish' is simply amazing. They understood our vision and turned it into a reality. Our website now showcases our brand in the best possible way. The attention to detail and the seamless user experience have made a <span>significant impact on our business</span>. We couldn't be happier with the results.",
      name: "Sophia L.",
      company: "Ecommerce Solutions Ltd.",
    },
    {
      quote:
        "Choosing 'Pencil to Polish' was the best decision we made for our mobile app. Their creative designs and user-centric approach have made our app stand out from the competition. The positive feedback from our users has been overwhelming, and our <span>app downloads have doubled</span> in just a month. We highly recommend their services.",
      name: "Michael R.",
      company: "Tech Innovators",
    },
  ];

  const handleNext = () => {
    setDirection(1);
    setActiveIndex((prevIndex) => (prevIndex + 1) % client_testimonials.length);
  };

  const handlePrevious = () => {
    setDirection(-1);
    setActiveIndex(
      (prevIndex) =>
        (prevIndex - 1 + client_testimonials.length) %
        client_testimonials.length
    );
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    const { clientX } = event;
    const { width } = event.currentTarget.getBoundingClientRect();
    const side = clientX < width / 2 ? "left" : "right";
    setHoverSide(side);
  };

  const handleClick = () => {
    if (hoverSide === "left") {
      handlePrevious();
    } else if (hoverSide === "right") {
      handleNext();
    }
  };

  const variants = {
    exit: (direction: number) => ({
      x: direction === 1 ? "100%" : "-100%",
      opacity: 0,
    }),
  };

  return (
    <PageSectionBG
      bgcolor={theme.palette.primary.main}
      paddingTop={3}
      paddingBottom={0}
      id="testimonials-section"
    >
      <Box
        component={"div"}
        sx={{
          background: theme.palette.primary.main,
          position: "relative",
          maxHeight: "660px",
          overflow: "hidden",
          height: "100vh",
          width: "100%",
          cursor:
            hoverSide === "left"
              ? "url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjgiIGhlaWdodD0iMjgiIHZpZXdCb3g9IjAgMCAyOCAyOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTI3LjMzMzcgMTIuMzMzNEg3LjA1MDMzTDE2LjM2NyAzLjAxNjc1TDE0LjAwMDMgMC42NjY3NDhMMC42NjY5OTIgMTQuMDAwMUwxNC4wMDAzIDI3LjMzMzRMMTYuMzUwMyAyNC45ODM0TDcuMDUwMzMgMTUuNjY2N0gyNy4zMzM3VjEyLjMzMzRaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K), auto"
              : hoverSide === "right"
              ? "url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjgiIGhlaWdodD0iMjgiIHZpZXdCb3g9IjAgMCAyOCAyOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0LjAwMDMgMC42NjY3NDhMMTEuNjUwMyAzLjAxNjc1TDIwLjk1MDMgMTIuMzMzNEgwLjY2Njk5MlYxNS42NjY3SDIwLjk1MDNMMTEuNjUwMyAyNC45ODM0TDE0LjAwMDMgMjcuMzMzNEwyNy4zMzM3IDE0LjAwMDFMMTQuMDAwMyAwLjY2Njc0OFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=), auto"
              : "auto",
        }}
        onMouseLeave={() => setHoverSide(null)}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
      >
        <Container maxWidth="xl">
          <Stack direction={"column"} spacing={2}>
            <Grid2
              style={{ height: "-webkit-fill-available" }}
              paddingTop={12}
              container
            >
              <Grid2 xs={12} lg={3}>
                <Typography
                  className="noselect"
                  lineHeight={0.9}
                  variant="xl2"
                  color="white"
                >
                  Kind
                  <br />
                  Words
                </Typography>
              </Grid2>
              <Grid2
                position="relative"
                xsOffset={0}
                lgOffset={1}
                xs={12}
                lg={6}
              >
                <AnimatePresence initial={false} custom={direction} mode="wait">
                  {client_testimonials.map(
                    (testimonial, index) =>
                      activeIndex === index && (
                        <motion.div
                          key={index}
                          initial={{
                            opacity: 0,
                            y: "-10%",
                          }}
                          animate={{
                            opacity: 1,
                            x: 0,
                            y: 0,
                          }}
                          variants={variants}
                          transition={{
                            initial: { duration: 0.5, delay: 0.5 },
                            animate: { duration: 0.5 },
                            exit: { duration: 0.5 },
                          }}
                          exit="exit"
                          style={{ position: "relative" }}
                        >
                          <Stack width="inherit">
                            <Quote
                              variant="textXLLight"
                              lineHeight={1.9}
                              color="white"
                            >
                              <span
                                style={{ fontWeight: "inherit" }}
                                dangerouslySetInnerHTML={{
                                  __html: testimonial.quote,
                                }}
                              ></span>
                            </Quote>
                            <Typography
                              sx={{ marginTop: 2 }}
                              fontWeight={"700"}
                              color="white"
                              variant="h6"
                            >
                              - {testimonial.name}, {testimonial.company}
                            </Typography>
                          </Stack>
                        </motion.div>
                      )
                  )}
                </AnimatePresence>
              </Grid2>
            </Grid2>
            <Grid2 paddingBottom={12} container mt={-2}>
              <Grid2
                style={{
                  position: "absolute",
                  bottom: "6rem",
                  width: "100%",
                  left: "0",
                }}
                justifyContent={"center"}
                display={"flex"}
                flexGrow={1}
                xs={12}
              >
                <Stack
                  justifyContent="center"
                  alignItems="start"
                  marginTop="auto"
                  direction="row"
                  gap={2}
                >
                  {client_testimonials.map((_, index) => (
                    <Indicator
                      key={index}
                      theme={theme}
                      active={(activeIndex === index).toString()}
                    />
                  ))}
                </Stack>
              </Grid2>
            </Grid2>
          </Stack>
        </Container>
      </Box>
    </PageSectionBG>
  );
};

export default TestimonialsSection;

interface IndicatorProps {
  theme: any;
  active: string;
}

const Indicator = styled(Box)<IndicatorProps>`
  background-color: ${(props) =>
    props.active === "true" ? props.theme.palette.green.main : "white"};
  width: ${pxToRem(15)};
  height: ${pxToRem(15)};
  border-radius: 50%;
`;

const Quote = styled(Typography)`
  span span {
    font-weight: bold;
    color: #4cff3c;
  }
`;
